import {ConsentChoice, isTrackingAllowed, persistConsent} from "@/lib/analytics/consent";
import {sanitizeProperties} from "@/lib/analytics/sanitize";

/**
 * Mixpanel (client-side) — AC-711.
 *
 * Sibling of `customerio.ts`, and deliberately shaped like it: env-gated enable
 * flag, client-only, and a hard guarantee that a blocked or failed vendor SDK
 * can never break a render or throw into a click handler. A large share of
 * casino traffic runs an ad-blocker, and Mixpanel is a commonly blocked host —
 * so "the SDK is missing" is the normal case, not the edge case, and every
 * entry point below is written to no-op rather than throw.
 *
 * Consent: init happens in an OPTED-OUT state unless tracking is already
 * allowed (see `consent.ts`). Mixpanel's own `opt_out_tracking_by_default` /
 * `opt_out_persistence_by_default` mean that in that state the SDK sends no
 * events and writes no cookies at all — it is not merely queuing them.
 *
 * Identity: anonymous visitors track under the shared `anonymous_id` cookie;
 * `identifyUser` is called with the JWT `sub` on login/registration so the
 * pre-registration journey merges into the player profile, and `resetIdentity`
 * is called on logout so the next visitor on a shared device does not inherit
 * the previous player's identity.
 *
 * ─── Loading (AC-803) ───────────────────────────────────────────────────────
 * The SDK is NOT part of the page's initial JavaScript. Two deliberate choices:
 *
 *  1. The `mixpanel-with-async-recorder` entry instead of the default one. The
 *     default entry inlines the whole rrweb session recorder (~410 KB emitted)
 *     even though `record_sessions_percent` is 0; this entry fetches the
 *     recorder from Mixpanel's CDN, and only when a percent > 0 is configured.
 *  2. `initMixpanel` `import()`s the SDK instead of holding a static import, so
 *     it lands in its own chunk fetched after hydration rather than in the
 *     shell every visitor downloads before first paint.
 *
 * Everything below stays SYNCHRONOUS and never-throwing for callers: calls made
 * before the SDK resolves are queued in order and replayed on load, so no call
 * site had to change. If the import fails (ad-blocker, offline) the queue is
 * dropped and every entry point degrades to the same no-op as before.
 */

/**
 * Module shape of the async-recorder entry. Derived from the `.cjs.d.ts` that
 * ships next to it rather than hand-written, so the SDK surface stays typed.
 */
type MixpanelSdk = typeof import("mixpanel-browser/dist/mixpanel-with-async-recorder.cjs.js")["default"];

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN;

/**
 * Mixpanel is only live when a token is configured AND analytics are enabled
 * for this environment. `NEXT_PUBLIC_ENABLE_MIXPANEL_ANALYTICS` is the existing flag
 * that already gates GA, so dev/local stay silent by default.
 */
export const isMixpanelEnabled = (): boolean =>
    !!MIXPANEL_TOKEN && process.env.NEXT_PUBLIC_ENABLE_MIXPANEL_ANALYTICS === "true";

/** Resolved and initialised SDK. Null until the dynamic import lands. */
let sdk: MixpanelSdk | null = null;
/** Guards the import against the many entry points that all call `initMixpanel`. */
let loadStarted = false;
/** Terminal state: the chunk could not be loaded or `init()` threw. */
let loadFailed = false;

/**
 * Calls made before the SDK resolved, in call order. Order matters — a queued
 * `reset()` must not overtake the `register()` that follows it (see the ordering
 * note in `MixpanelInitializer`).
 */
let pendingCalls: Array<(mp: MixpanelSdk) => void> = [];

/**
 * The window between the first call and the chunk landing is one navigation at
 * most, so a healthy queue is a handful of entries. The cap only exists so that
 * a pathological case (SDK never resolves, user browses for an hour) cannot grow
 * the array without bound; the oldest calls are the valuable ones, so overflow
 * drops the newest.
 */
const MAX_PENDING_CALLS = 50;

/**
 * Idempotent init. Safe to call from anywhere, including from a track() that
 * races the initializer's effect — which is why every entry point calls it.
 *
 * Returns immediately: the actual SDK load is asynchronous.
 */
export const initMixpanel = (): void => {
    if (loadStarted || typeof window === "undefined" || !isMixpanelEnabled()) return;

    loadStarted = true;
    void loadSdk();
};

const loadSdk = async (): Promise<void> => {
    const token = MIXPANEL_TOKEN;
    if (!token) return;

    try {
        const {default: mixpanel} = await import(
            "mixpanel-browser/dist/mixpanel-with-async-recorder.cjs.js"
        );

        // Evaluated here rather than at `initMixpanel` time so that a consent
        // decision taken while the chunk was in flight is already reflected in
        // the SDK's starting state.
        const optedOut = !isTrackingAllowed();

        mixpanel.init(token, {
            // Keeps EU data residency available without a code change; unset = US.
            ...(process.env.NEXT_PUBLIC_MIXPANEL_API_HOST
                ? {api_host: process.env.NEXT_PUBLIC_MIXPANEL_API_HOST}
                : {}),
            persistence: "localStorage",
            // The consent gate. In `required` mode with no decision yet, this
            // means: no events, and no persistence written either.
            opt_out_tracking_by_default: optedOut,
            opt_out_persistence_by_default: optedOut,
            // We emit a curated, typed event catalogue. Autocapture would flood
            // the project (Mixpanel bills per event) and is the single easiest
            // way to accidentally capture PII out of the DOM.
            autocapture: false,
            track_pageview: false,
            // Session Replay. Defaults to 0 (off) — set a percent to record. With
            // this entry the recorder is fetched from Mixpanel's CDN on demand, so
            // 0 costs nothing. Masks inputs by default; we additionally mask ALL
            // text because deposit, withdrawal and KYC screens render balances,
            // amounts and PII as text.
            record_sessions_percent: Number(process.env.NEXT_PUBLIC_MIXPANEL_REPLAY_PERCENT) || 0,
            record_mask_text_selector: "*",
            debug: process.env.NEXT_PUBLIC_APP_ENV !== "prod",
        });

        sdk = mixpanel;

        const queued = pendingCalls;
        pendingCalls = [];
        queued.forEach((call) => call(mixpanel));
    } catch (error) {
        loadFailed = true;
        pendingCalls = [];
        console.warn("[mixpanel] init failed — analytics disabled for this session", error);
    }
};

/**
 * Runs `operation` against the SDK, now or as soon as it loads. The single place
 * that swallows vendor errors, so no entry point can throw into a caller.
 */
const withMixpanel = (label: string, operation: (mp: MixpanelSdk) => void): void => {
    if (typeof window === "undefined" || !isMixpanelEnabled() || loadFailed) return;

    initMixpanel();

    const run = (mp: MixpanelSdk): void => {
        try {
            operation(mp);
        } catch (error) {
            console.warn(`[mixpanel] ${label} failed`, error);
        }
    };

    if (sdk) {
        run(sdk);
        return;
    }

    if (pendingCalls.length >= MAX_PENDING_CALLS) return;
    pendingCalls.push(run);
};

/**
 * True only when the SDK is live (or on its way) AND the visitor has allowed
 * tracking. Consent is evaluated at CALL time, not at flush time, so a queued
 * call carries the decision that was in force when it was made.
 */
const canTrack = (): boolean => {
    initMixpanel();
    return !loadFailed && isTrackingAllowed();
};

/**
 * Records the visitor's consent decision and flips Mixpanel's opt-in/opt-out
 * state to match. This is the entry point the cookie banner calls.
 *
 * Only the `analytics` category is consulted here — `marketing` is persisted for
 * the follow-up that attaches Customer.io, and gates nothing today.
 *
 * Granting consent mid-session initialises the SDK if the earlier init was
 * skipped, so events fired straight after the banner is dismissed are captured.
 * Withdrawing it calls `opt_out_tracking()`, which also clears the persistence
 * Mixpanel had already written.
 */
export const applyConsentDecision = (choice: ConsentChoice): void => {
    persistConsent(choice);

    if (!isMixpanelEnabled() || typeof window === "undefined") return;

    withMixpanel("consent decision", (mp) => {
        if (choice.analytics) {
            mp.opt_in_tracking();
            // Replay the common properties the initializer tried to register
            // while we were still opted out — see `superProperties`.
            if (Object.keys(superProperties).length) {
                mp.register(sanitizeProperties(superProperties));
            }
        } else {
            mp.opt_out_tracking();
        }
    });
};

/**
 * The last super-property bag we were asked to register, kept in memory.
 *
 * Needed because the initializer registers common properties on mount — while,
 * in `required` mode, consent is still unknown and the SDK is opted out, so the
 * register is a no-op. Without this cache, a visitor who accepts in the banner
 * would send events with NO common properties until an unrelated dependency
 * (locale, currency, login) happened to re-register them. `applyConsentDecision`
 * replays this bag on opt-in.
 */
let superProperties: Record<string, unknown> = {};

/**
 * Registers super-properties — sent with every subsequent event. Used for the
 * common-properties layer (platform, country, currency, environment, …).
 */
export const registerSuperProperties = (properties: Record<string, unknown>): void => {
    superProperties = {...superProperties, ...properties};

    if (!canTrack()) return;

    // Sanitized here rather than inside the closure so a queued call carries the
    // payload as it was at call time.
    const payload = sanitizeProperties(properties);
    withMixpanel("register", (mp) => mp.register(payload));
};

/**
 * Binds the anonymous journey to the player. Mixpanel merges the pre-identify
 * events into this profile, so the registration funnel keeps its anonymous head.
 *
 * `userId` must be the stable DB key (the JWT `sub`) — never an email, never a
 * per-session value. Getting this wrong corrupts the identity graph permanently.
 */
export const identifyUser = (userId: string, traits: Record<string, unknown> = {}): void => {
    if (!canTrack()) return;

    const profile = Object.keys(traits).length ? sanitizeProperties(traits) : null;

    withMixpanel("identify", (mp) => {
        mp.identify(userId);
        if (profile) {
            mp.people.set(profile);
        }
    });
};

/**
 * Clears the identity on logout so a subsequent anonymous visitor on the same
 * device is not attributed to the player who just left.
 *
 * Only meaningful once an init has been attempted — a logout on a page where
 * Mixpanel was never started has no identity to clear.
 */
export const resetIdentity = (): void => {
    if (!loadStarted || !isMixpanelEnabled()) return;

    withMixpanel("reset", (mp) => mp.reset());
};

/**
 * Low-level track. Prefer the typed helpers in `mixpanel-events.ts` — they make
 * a mistyped event name or a missing property a compile error rather than a
 * silently broken funnel.
 *
 * Never throws: analytics must not be able to break a user-facing interaction.
 */
export const trackEvent = (event: string, properties: Record<string, unknown> = {}): void => {
    if (!canTrack()) return;

    const payload = sanitizeProperties(properties);
    withMixpanel(`track("${event}")`, (mp) => mp.track(event, payload));
};
